/**
 * Service Error
 * Structured error type for service layer operations
 */

export class ServiceError extends Error {
  constructor(
    public readonly code: string,
    public readonly message: string,
    public readonly originalError?: unknown,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ServiceError';

    // Maintain proper stack trace for where error was thrown (Node.js only)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ServiceError);
    }
  }

  /**
   * Convert to JSON for API responses
   */
  toJSON() {
    return {
      code: this.code,
      message: this.message,
      details: this.details,
    };
  }

  /**
   * Check if error is of a specific code
   */
  is(code: string): boolean {
    return this.code === code;
  }

  /**
   * Create common error types
   */
  static notFound(
    message: string,
    details?: Record<string, unknown>,
  ): ServiceError {
    return new ServiceError('NOT_FOUND', message, undefined, details);
  }

  static validation(
    message: string,
    details?: Record<string, unknown>,
  ): ServiceError {
    return new ServiceError('VALIDATION_ERROR', message, undefined, details);
  }

  static unauthorized(message = 'Unauthorized'): ServiceError {
    return new ServiceError('UNAUTHORIZED', message);
  }

  static forbidden(message = 'Forbidden'): ServiceError {
    return new ServiceError('FORBIDDEN', message);
  }

  static conflict(
    message: string,
    details?: Record<string, unknown>,
  ): ServiceError {
    return new ServiceError('CONFLICT', message, undefined, details);
  }

  static database(message: string, originalError?: unknown): ServiceError {
    return new ServiceError('DATABASE_ERROR', message, originalError);
  }

  static internal(message: string, originalError?: unknown): ServiceError {
    return new ServiceError('INTERNAL_ERROR', message, originalError);
  }

  static expired(
    message: string,
    details?: Record<string, unknown>,
  ): ServiceError {
    return new ServiceError('EXPIRED', message, undefined, details);
  }
}

/**
 * Common error codes used throughout the application
 */
export enum ErrorCode {
  // Authentication & Authorization
  UNAUTHORIZED = 'UNAUTHORIZED',
  FORBIDDEN = 'FORBIDDEN',

  // Resource errors
  NOT_FOUND = 'NOT_FOUND',
  ALREADY_EXISTS = 'ALREADY_EXISTS',
  CONFLICT = 'CONFLICT',

  // Validation errors
  VALIDATION_ERROR = 'VALIDATION_ERROR',
  INVALID_INPUT = 'INVALID_INPUT',
  MISSING_REQUIRED_FIELD = 'MISSING_REQUIRED_FIELD',

  // Business logic errors
  BUSINESS_RULE_VIOLATION = 'BUSINESS_RULE_VIOLATION',
  INVALID_STATUS = 'INVALID_STATUS',
  INVALID_TRANSITION = 'INVALID_TRANSITION',

  // Casting-specific errors
  DANCER_NOT_AVAILABLE = 'DANCER_NOT_AVAILABLE',
  SCHEDULE_CONFLICT = 'SCHEDULE_CONFLICT',
  REQUIREMENTS_NOT_MET = 'REQUIREMENTS_NOT_MET',
  ALREADY_ASSIGNED = 'ALREADY_ASSIGNED',
  HIRE_ORDER_CONFLICT = 'HIRE_ORDER_CONFLICT',
  ASSIGNMENT_NOT_FOUND = 'ASSIGNMENT_NOT_FOUND',

  // Event-specific errors
  EVENT_NOT_FOUND = 'EVENT_NOT_FOUND',
  EVENT_CANCELLED = 'EVENT_CANCELLED',
  EVENT_COMPLETED = 'EVENT_COMPLETED',
  PARTICIPATION_EXISTS = 'PARTICIPATION_EXISTS',
  PARTICIPATION_NOT_FOUND = 'PARTICIPATION_NOT_FOUND',
  MAX_PARTICIPANTS_REACHED = 'MAX_PARTICIPANTS_REACHED',
  REGISTRATION_CLOSED = 'REGISTRATION_CLOSED',
  INVITATION_NOT_FOUND = 'INVITATION_NOT_FOUND',
  INVITATION_ALREADY_RESPONDED = 'INVITATION_ALREADY_RESPONDED',
  ACCOUNT_ISOLATION_VIOLATION = 'ACCOUNT_ISOLATION_VIOLATION',
  CONCURRENT_MODIFICATION = 'CONCURRENT_MODIFICATION',

  // Database errors
  DATABASE_ERROR = 'DATABASE_ERROR',
  DUPLICATE_ENTRY = 'DUPLICATE_ENTRY',
  FOREIGN_KEY_VIOLATION = 'FOREIGN_KEY_VIOLATION',
  // Transient infrastructure unavailability (e.g. DB returning HTML instead of JSON)
  SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE',

  // System errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  EXTERNAL_SERVICE_ERROR = 'EXTERNAL_SERVICE_ERROR',
  EXPIRED = 'EXPIRED',

  // Legal compliance errors
  JURISDICTION_NOT_FOUND = 'JURISDICTION_NOT_FOUND',
  JURISDICTION_CONFLICT = 'JURISDICTION_CONFLICT',
  ENGAGEMENT_MODEL_NOT_FOUND = 'ENGAGEMENT_MODEL_NOT_FOUND',
  ENGAGEMENT_MODEL_CONFLICT = 'ENGAGEMENT_MODEL_CONFLICT',
  PER_DIEM_RATE_NOT_FOUND = 'PER_DIEM_RATE_NOT_FOUND',
  PER_DIEM_RATE_CONFLICT = 'PER_DIEM_RATE_CONFLICT',
  CLIENT_JURISDICTION_NOT_FOUND = 'CLIENT_JURISDICTION_NOT_FOUND',
  CLIENT_JURISDICTION_CONFLICT = 'CLIENT_JURISDICTION_CONFLICT',
  REIMBURSEMENT_NOT_FOUND = 'REIMBURSEMENT_NOT_FOUND',
  REIMBURSEMENT_CONFLICT = 'REIMBURSEMENT_CONFLICT',

  // Fee calculation errors
  FEE_CALCULATION_ERROR = 'FEE_CALCULATION_ERROR',
  FIRST_TIME_CHECK_ERROR = 'FIRST_TIME_CHECK_ERROR',

  // Invoice errors
  INVOICE_CREATE_ERROR = 'INVOICE_CREATE_ERROR',
  INVOICE_NOT_FOUND = 'INVOICE_NOT_FOUND',
  FETCH_ERROR = 'FETCH_ERROR',

  // Legal status errors
  LEGAL_STATUS_TYPE_NOT_FOUND = 'LEGAL_STATUS_TYPE_NOT_FOUND',
  LEGAL_STATUS_TYPE_CONFLICT = 'LEGAL_STATUS_TYPE_CONFLICT',
  DANCER_LEGAL_STATUS_NOT_FOUND = 'DANCER_LEGAL_STATUS_NOT_FOUND',
  DANCER_LEGAL_STATUS_CONFLICT = 'DANCER_LEGAL_STATUS_CONFLICT',
  DANCER_LEGAL_STATUS_INVALID_TRANSITION = 'DANCER_LEGAL_STATUS_INVALID_TRANSITION',

  // Contract errors
  CONTRACT_TEMPLATE_NOT_FOUND = 'CONTRACT_TEMPLATE_NOT_FOUND',
  CONTRACT_TEMPLATE_CONFLICT = 'CONTRACT_TEMPLATE_CONFLICT',
  GENERATED_CONTRACT_NOT_FOUND = 'GENERATED_CONTRACT_NOT_FOUND',
}
